// SignalDefinitionPanel — the signal's actual definition, shown in the explore
// view's RIGHT detail panel on a signal scope so you read the code beside the
// live traffic it fires on. Signals are native + read-only, so this is a
// read-only view of the built-in definition plus a sample tester.
//
// The source is the BACKEND's (ListSignalVersions, newest version, via
// useSignalSource) - the same source the zygote runs - so the definition shown
// and the sample tester share one source of truth (the dashboard embeds no copy
// of any detector). The sample tester runs it in the in-browser Pyodide kernel
// against a synthetic event whose every text view is the pasted sample, so a
// match can be verified without hunting for a real prompt.

import { useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import Sheet from "../Sheet.jsx";
import SignalSourceEditor from "../SignalSourceEditor.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useSignalCatalog } from "../../lib/signalCatalog.js";
import { signalSourceDefinition, useSignalSource } from "../../lib/useSignalSource.js";
import { runTrigger } from "../../lib/triggers/triggerHarness.js";

const GHOST_BTN = "inline-flex min-h-[28px] items-center cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[11px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 disabled:cursor-wait";

function Chip({ children }) {
  return <span className="border border-[color:var(--line-soft)] px-[5px] py-[1px] text-[10px] text-[color:var(--muted-soft)]">{children}</span>;
}

export default function SignalDefinitionPanel({ command }) {
  const { ready, denied, error } = useSignalCatalog();
  // The read-only source, fetched from the backend (newest version); see
  // useSignalSource for the null / "" / string states.
  const { meta, source, error: sourceErr } = useSignalSource(command);
  const def = signalSourceDefinition(meta, source);

  const [testText, setTestText] = useState("");
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  if (denied) {
    return (
      <div className="grid h-full place-items-center px-[12px]">
        <p className={`${NOTE_CLASS} max-w-[360px] text-center`}>
          You don’t have permission to view signal definitions.
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="grid h-full place-items-center px-[12px]">
        <p className={`${NOTE_CLASS} max-w-[360px] text-center`}>
          Couldn’t load signals. Try again shortly.
        </p>
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="grid h-full place-items-center px-[12px]" aria-busy="true">
        <p className={`${NOTE_CLASS} max-w-[360px] text-center`}>Loading signals…</p>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="grid h-full place-items-center px-[12px]">
        <p className={`${NOTE_CLASS} max-w-[360px] text-center`}>
          {command ? `No signal named /${command} for this tenant.` : "No signal selected."}
        </p>
      </div>
    );
  }

  // Evaluate the definition against a synthetic event whose every text view is
  // the pasted sample — works whatever scope the signal reads.
  async function runTest() {
    if (!def || !def.steps[0].code) { setTestResult({ error: "No source available." }); return; }
    const sample = testText || "";
    const event = {
      event_id: "sample", endpoint_id: "sample-endpoint", session_id: "sample-session",
      event_time: new Date().toISOString(),
      provider: "sample",
      text: sample, last_text: sample, last_user_text: sample,
      turns: [{ role: "user", text: sample }], tools: [], settings: null, body_raw: "",
    };
    setTestRunning(true);
    setTestResult(null);
    try {
      const r = await runTrigger(def, event);
      setTestResult(r.error ? { error: r.error.message || String(r.error) } : { fired: r.fired, annotation: r.annotation });
    } catch (e) {
      setTestResult({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      setTestRunning(false);
    }
  }

  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid min-w-0 content-start gap-[10px] py-[10px] pb-[18px] [grid-template-columns:minmax(0,1fr)]" aria-busy={source == null}>
      <div className="flex flex-wrap gap-[5px]">
        <Chip>/{meta.command}</Chip>
        <Chip>python</Chip>
        <Chip>cheap · every window</Chip>
        {meta.tier ? <Chip>{meta.tier}</Chip> : null}
        <Chip>built-in</Chip>
      </div>
      {source == null ? (
        <span className={NOTE_CLASS}>Loading definition…</span>
      ) : (
        <>
          <SignalSourceEditor key={meta.command} value={def} onChange={() => {}} readOnly />
          <span className={NOTE_CLASS}>
            Built-in signal. Read-only.{sourceErr ? " Source is currently unavailable." : ""}
          </span>

          <Sheet eyebrow="Test" title="Run against sample text" tight>
            <textarea
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              spellCheck={false}
              placeholder="Paste sample text, such as a fake AKIAIOSFODNN7EXAMPLE or rm -rf /"
              className="w-full min-h-[80px] resize-y border border-[color:var(--line)] bg-[color:var(--bg)] p-[8px] font-mono text-[11px] leading-[1.4] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
            />
            <div className="mt-[8px] flex flex-wrap items-center gap-[8px]">
              <button type="button" onClick={runTest} disabled={testRunning || !!sourceErr} className={GHOST_BTN}>
                {testRunning ? "Testing…" : "Test"}
              </button>
              {testResult ? (
                testResult.error ? (
                  <span className="font-mono text-[11px] text-[color:var(--red,#c0392b)]">{testResult.error}</span>
                ) : testResult.fired ? (
                  <span className="inline-flex items-center gap-[6px] text-[11px] text-[color:var(--green)]">
                    fired
                    {testResult.annotation?.type ? <span className="font-mono text-[color:var(--ink)]">{testResult.annotation.type}</span> : null}
                  </span>
                ) : (
                  <span className={NOTE_CLASS}>no match</span>
                )
              ) : null}
            </div>
            {testResult?.fired && testResult.annotation ? (
              <pre className="mt-[8px] overflow-x-auto border border-[color:var(--line)] bg-[color:var(--bg)] p-[8px] font-mono text-[10px] leading-[1.4] text-[color:var(--muted)]">
                {JSON.stringify(testResult.annotation, null, 2)}
              </pre>
            ) : null}
          </Sheet>
        </>
      )}
    </ScrollArea>
  );
}
